<template>
  <v-dialog v-model="show" width="500">
    <v-card>
      <v-card-title class="third white--text">
        Changer le statut de présence du {{ selectedParticipant.participantFullname }}
      </v-card-title>
      <v-card-text class="mt-4 pa-0">
        <div class="px-6">
          <p>Le statut sera changé en '{{ presenceStatus }}'</p>
          <p>Êtes-vous sûr de vouloir poursuivre cette opération ?</p>
        </div>
        <div v-if="loading" class="d-flex justify-center align-center mt-2">
          <Loader />
        </div>

        <div v-else-if="recupProductEligible">
          <v-divider />
          <div class="px-6 mt-4">
            <div v-if="availableRecuperations === 0" class="flex my-4">
              <p class="text-pre-line">{{ getUnavailableRecuperationMessage() }}</p>
            </div>
            <div v-else-if="availableRecuperations > 0">
              <span>Souhaitez vous donner un rattrapage à {{ selectedParticipant.participantFullname }} ?</span>
              <v-radio-group v-model="giveRecuperationProduct" row color="third">
                <v-radio
                  label="Oui"
                  :value="true"
                  color="third"
                />
                <v-radio
                  label="Non"
                  :value="false"
                  color="third"
                />
              </v-radio-group>
            </div>
            <div v-else>
              <v-icon color="yellow">mdi-alert</v-icon>
              <span class="third--text">Une erreur s'est produite lors de la récupération du nombre de récupérations de produits pour ce client.</span>
            </div>
          </div>
          <v-divider />
        </div>
      </v-card-text>
      <v-card-actions class="justify-end">
        <v-btn outlined rounded class="third--text" :disabled="loading" @click="closeModal">
          <v-icon>mdi-arrow-u-left-top</v-icon>
          Annuler
        </v-btn>
        <v-btn outlined rounded class="third--text" :disabled="loading" @click="sessionPresenceChanged">
          <v-icon>mdi-plus-circle-outline</v-icon>
          Confirme
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { ref, onMounted } from '@vue/composition-api';
import { format } from 'date-fns';
import Loader from '@/components/common/Loader.vue';
import { sendNotification, changePresenceInSession, hasRecuperationProduct, computeRecuperationProductDetails } from '@/helpers/utils';
import { updateSessionAmplify } from '@/helpers/amplify';
import { getProductRecuperations, createProductRecuperation, getContractByNumber } from '@/helpers/request';

export default {
  components: { Loader },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    presenceStatus: {
      type: String,
      default: ''
    },
    selectedParticipant: {
      type: Object,
      default: () => {}
    },
    session: {
      type: Object,
      default: () => {}
    }
  },
  setup (props, context) {
    const loading = ref(false);
    const giveRecuperationProduct = ref(false);
    const availableRecuperations = ref(null);
    const contract = ref(null);
    const recupProductEligible = ref(false);
    const recuperationProductDetails = ref(null);
    const errorCode = ref(null);

    const closeModal = () => {
      context.emit('closeModal');
    };

    const getUnavailableRecuperationMessage = () => {
      switch (errorCode.value) {
        case 'NOT_IN_CONTRACT_INTERVAL': {
          const validityStart = contract.value?.start ? format(new Date(contract.value.start), 'dd/MM/yyyy') : '-';
          const validityEnd = contract.value?.end ? format(new Date(contract.value.end), 'dd/MM/yyyy') : '-';
          return `Impossible de générer le bon de récupération.
          La date de la demande doit être comprise entre la date de début et la date de fin de validité du contrat. 
          Contrat valide du ${validityStart} au ${validityEnd}.`;
        }
        default:
          return `Le client ${props.selectedParticipant.participantFullname} a déja bénéficié d'un rattrapage au cours de ce trimestre, il ne peut donc pas obtenir un second.`;
      }
    };

    const sessionPresenceChanged = async () => {
      const updatedSession = changePresenceInSession(props.selectedParticipant, props.session, props.presenceStatus);

      if (!updatedSession.updated) {
        sendNotification('error', 'Aucune session à mettre à jour.');
        return;
      }

      let recupResponse = null;
      loading.value = true;

      if (giveRecuperationProduct.value && recuperationProductDetails.value) {
        const response = await createProductRecuperation(props.selectedParticipant.participant, props.selectedParticipant.contract_number, recuperationProductDetails.value);

        if (!response.success) {
          loading.value = false;
          sendNotification('error', 'Erreur lors de la création d\'une entrée de récupération sur le client.');
          return;
        }
      }

      await updateSessionAmplify(updatedSession.session);

      loading.value = false;

      closeModal();
      sendNotification('success', `Présence mise à jour avec succès${recupResponse && recupResponse.success ? ' et produit de récupération attribué au client.' : '.'}`);
    };

    onMounted(async () => {
      if (!props.selectedParticipant.contract_number) {
        return;
      }

      loading.value = true;

      const contractDetails = await getContractByNumber(props.selectedParticipant.participant, props.selectedParticipant.contract_number);

      if (!contractDetails.success) {
        sendNotification('error', 'Erreur lors de la récupération du contrat client.');
        loading.value = false;
        return;
      }

      if (await hasRecuperationProduct(contractDetails.data)) {
        const response = await getProductRecuperations(props.selectedParticipant.participant, props.selectedParticipant.contract_number);
        if (!response.success) {
          sendNotification('error', 'Erreur lors de la récupération des récupérations de produits.');
          loading.value = false;
          return;
        }
        contract.value = response.data.contract;
        availableRecuperations.value = response.data.numberOfRecuperationsAvailable;
        errorCode.value = response.data.errorCode;
        recuperationProductDetails.value = await computeRecuperationProductDetails(contract.value);
        recupProductEligible.value = true;
      }
      loading.value = false;
    });
    return { loading, sessionPresenceChanged, closeModal, hasRecuperationProduct, availableRecuperations, giveRecuperationProduct, recupProductEligible, getUnavailableRecuperationMessage };
  }
};
</script>
